import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import coverImage from "./../../images/blog/picforasana2.png"; // swap later if you add a new graphic
import coverWeb from "./../../images/blog/picforasana2-1280.webp";
import FAQ from "../../components/FAQs";
import { DEMO_LINK } from "../../components/demoBooking";
import CopyLinkButton from "../../components/CopyLinkButton";

export default function Blog_LessMeetings() {
  const title =
    "Fewer Meetings with BossDrop AI: Auto-Schedule by Risk, Availability, and Asana Tasks";
  const description =
    "BossDrop replaces status meetings with AI workflow automation - async updates, risk-aware scheduling, and Slack roll-ups so teams stay focused and ship faster.";
  const cover = coverImage;
  const canonicalUrl =
    "https://bossdrop.ai/blog/fewer-meetings-with-bossdrop-ai";
  const published = "2025-08-25";
  const readTime = "7 min read";

  const keywords = [
    "BossDrop AI",
    "AI for project management",
    "AI workflow automation",
    "autonomous work management",
    "work management",
    "workflow management",
    "project management tool",
    "best task management software",
    "team collaboration tools",
    "productivity software",
    "project tracking",
    "task management software",
  ];

  // share helpers
  const shareUrl = encodeURIComponent(canonicalUrl);
  const shareText = encodeURIComponent(title);
  const onShare = (net) => {
    const map = {
      x: `https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
    };
    window.open(map[net], "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    document.title = title;
    setCanonical(canonicalUrl);
    setMeta(description);
    setKeywords(keywords.join(", "));
    setOG("og:type", "article");
    setOG("og:title", title);
    setOG("og:description", description);
    setOG("og:url", canonicalUrl);
    setOG("og:image", toAbs(cover));
    setOG("twitter:card", "summary_large_image");
    setOG("twitter:title", title);
    setOG("twitter:description", description);
    setOG("twitter:image", toAbs(cover));

    injectLDJSON("ldjson-blogpost", {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: title,
      description,
      datePublished: published,
      dateModified: published,
      author: { "@type": "Organization", name: "BossDrop" },
      publisher: {
        "@type": "Organization",
        name: "BossDrop",
        logo: { "@type": "ImageObject", url: "https://bossdrop.ai/icon.png" },
      },
      image: toAbs(cover),
      mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
      keywords: keywords.join(", "),
      articleSection: "AI Project Management",
    });

    const els = Array.from(document.querySelectorAll(".reveal"));
    if (!("IntersectionObserver" in window) || !els.length) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <section className="page container">
      <nav
        className="breadcrumbs reveal"
        style={{ "--delay": "0ms" }}
        aria-label="Breadcrumb"
      >
        <Link to="/">Home</Link>
        <span>›</span>
        <Link to="/blog">Blog</Link>
        <span>›</span>
        <span aria-current="page">Fewer Meetings with BossDrop AI</span>
      </nav>

      {/* BossDrop-blue feature hero (uses your existing hero-feature styles) */}
      <div className="hero-feature reveal" style={{ "--delay": "40ms" }}>
        <div className="hero-gradient" />
        <div className="hero-grid">
          <div className="hero-card">
            <img
              src={coverWeb}
              alt="BossDrop AI auto-scheduling meetings based on risk and availability"
            />
          </div>

          <div className="hero-text">
            <div className="pill">MEETING OPS</div>
            <h1 className="h1">{title}</h1>

            <div className="byline">
              <div className="author">
                <div className="avatar" aria-hidden="true">
                  BD
                </div>
                <div>
                  <div className="author-name">BossDrop Team</div>
                  <div className="meta">
                    {new Date(published).toLocaleDateString()} • {readTime}
                  </div>
                </div>
              </div>

              <div className="share">
                <button aria-label="Share on X" onClick={() => onShare("x")}>
                  {iconX()}
                </button>
                <button
                  aria-label="Share on LinkedIn"
                  onClick={() => onShare("linkedin")}
                >
                  {iconLinkedIn()}
                </button>
                <button
                  aria-label="Share on Facebook"
                  onClick={() => onShare("facebook")}
                >
                  {iconFacebook()}
                </button>
                <CopyLinkButton url={canonicalUrl} />
              </div>
            </div>
          </div>
        </div>

        <div className="hero-cta">
          <span>
            Cut status meetings. Keep decisions. Let{" "}
            <strong>BossDrop AI</strong> schedule what actually matters.
          </span>
          <a
            className="btn btnDemo"
            {...DEMO_LINK}
            aria-label="Book a demo"
          >
            Book a demo
          </a>
        </div>
      </div>

      <article className="article">
        <p className="lede reveal" style={{ "--delay": "80ms" }}>
          Leaders tell us the real tax on execution is{" "}
          <strong>meeting bloat</strong>. BossDrop replaces recurring status
          calls with <strong>AI workflow automation</strong> - async updates in
          Slack, <strong>risk-aware scheduling</strong>, and crisp decision
          briefs. Fewer meetings ❌, more outcomes ✅.
        </p>

        <nav className="toc reveal" style={{ "--delay": "100ms" }}>
          <a href="#meeting-tax">The meeting tax</a>
          <a href="#replace-status">Replace status meetings with async</a>
          <a href="#auto-schedule">Auto-schedule by risk & availability</a>
          <a href="#playbooks">Playbooks</a>
          <a href="#metrics">Metrics to track</a>
          <a href="#faq">FAQ</a>
        </nav>

        <h2 id="meeting-tax" className="reveal" style={{ "--delay": "120ms" }}>
          The meeting tax (and why it spirals)
        </h2>
        <ul className="reveal bullets" style={{ "--delay": "140ms" }}>
          <li>
            👥 Calendar creep from weekly “syncs” that rarely change outcomes.
          </li>
          <li>📉 Context switching kills focus time and slows delivery.</li>
          <li>
            🔁 Meetings to prep for meetings (slides, status writes, standups).
          </li>
        </ul>

        {/* FIGURE 1 */}
        {/*         <figure className="figure reveal" style={{ "--delay": "160ms" }}>
          <img
            src={cover}
            loading="lazy"
            alt="Status roll-up replacing live meeting updates"
            width="1200"
            height="675"
          />
          <figcaption>
            BossDrop roll-ups turn noisy threads into one decision-ready update.
          </figcaption>
        </figure> */}

        <h2
          id="replace-status"
          className="reveal"
          style={{ "--delay": "180ms" }}
        >
          Replace status meetings with async updates
        </h2>
        <ul className="reveal checks" style={{ "--delay": "200ms" }}>
          <li>
            ✅ <strong>Slack roll-ups</strong> with progress, blockers, and
            owner next steps.
          </li>
          <li>
            ✅ <strong>Auto-nudges</strong> to collect updates before a
            deadline - no “round robin”.
          </li>
          <li>
            ✅ <strong>Decision packets</strong> (context + trade-offs) when
            leadership input is actually needed.
          </li>
        </ul>

        {/* FIGURE 2 */}
        {/*         <figure className="figure reveal" style={{ "--delay": "220ms" }}>
          <img
            src={cover}
            loading="lazy"
            alt="Async updates gathered from Asana and Slack into a single brief"
            width="1200"
            height="675"
          />
          <figcaption>
            {"Async > live: updates pulled from Asana tasks + Slack into a single brief."}
          </figcaption>
        </figure> */}

        <h2
          id="auto-schedule"
          className="reveal"
          style={{ "--delay": "240ms" }}
        >
          Auto-schedule by risk, team availability, and Asana tasks
        </h2>
        <ul className="reveal bullets" style={{ "--delay": "260ms" }}>
          <li>
            🛑 <strong>Risk-aware windows:</strong> BossDrop schedules only if
            risk ≥ threshold (miss probability, MTTR, dependency drift).
          </li>
          <li>
            🧩 <strong>Asana-linked agenda:</strong> open tasks → agenda items
            with owners & timeboxes.
          </li>
          <li>
            🗓️ <strong>Availability-aware:</strong> respects timezones, working
            hours, PTO, and existing holds.
          </li>
          <li>
            🔔 <strong>Only critical meetings:</strong> incident review,
            go/no-go, dependency unblock - everything else stays async.
          </li>
        </ul>

        {/* FIGURE 3 */}
        <figure className="figure reveal" style={{ "--delay": "280ms" }}>
          <img
            src={coverWeb}
            loading="lazy"
            alt="Risk-aware scheduler choosing the earliest effective slot"
            width="1200"
            height="675"
          />
          <figcaption>
            Risk & availability determine the earliest <em>effective</em>{" "}
            slot - not just the first open slot.
          </figcaption>
        </figure>

        <h2 id="playbooks" className="reveal" style={{ "--delay": "300ms" }}>
          Playbooks you can turn on today
        </h2>
        <ol className="reveal numbered" style={{ "--delay": "320ms" }}>
          <li>
            <strong>Weekly status → Slack roll-up.</strong> Replace 60 min with
            a 2-min read + reactions.
          </li>
          <li>
            <strong>Launch room.</strong> Risk ≥ medium auto-creates a 15-min
            go/no-go with Asana agenda.
          </li>
          <li>
            <strong>Dependency unblock.</strong> If a task blocks ≥2 others for
            48h, schedule a 10-min unblock.
          </li>
          <li>
            <strong>Incident follow-up.</strong>{" "}
            {"MTTR > target? Schedule a short retro with action items."}
          </li>
        </ol>

        <h2 id="metrics" className="reveal" style={{ "--delay": "340ms" }}>
          Metrics to track (watch these drop)
        </h2>
        <ul className="reveal checklist" style={{ "--delay": "360ms" }}>
          <li>🕒 Meeting hours per person/week</li>
          <li>⏱️ Cycle time per task / project</li>
          <li>📅 On-time delivery rate</li>
          <li>🚨 Escalation count & time-to-resolve</li>
        </ul>

        <FAQ
          reveal={false}
          items={[
            {
              q: "Will BossDrop cancel meetings automatically?",
              a: "BossDrop proposes async replacements and only schedules live sessions when risk or a decision truly requires it. You approve the plan.",
            },
            {
              q: "Does it support Jira instead of Asana?",
              a: "Yes - BossDrop works with either Asana or Jira plus Slack. Scheduling also respects timezones, working hours, and PTO.",
            },
            {
              q: "How does it know risk?",
              a: "Signals from task age, dependency depth, owner workload, SLA breaches, and blocker history feed a tunable risk score used for roll-ups and auto-scheduling.",
            },
          ]}
        />

        <div className="cta-row reveal" style={{ "--delay": "460ms" }}>
          <a className="btn" {...DEMO_LINK}>
            Book a demo
          </a>
          <a className="btn btnGhost" href="/how-it-works">
            See how it works
          </a>
        </div>
      </article>
    </section>
  );
}

/* ---------- helpers ---------- */
function setMeta(content) {
  setOrCreate(
    'meta[name="description"]',
    (el) => el.setAttribute("name", "description"),
    content
  );
}
function setKeywords(content) {
  setOrCreate(
    'meta[name="keywords"]',
    (el) => el.setAttribute("name", "keywords"),
    content
  );
}
function setOG(property, content) {
  if (!content) return;
  const sel = property.startsWith("og:")
    ? `meta[property="${property}"]`
    : `meta[name="${property}"]`;
  setOrCreate(
    sel,
    (el) => {
      property.startsWith("og:")
        ? el.setAttribute("property", property)
        : el.setAttribute("name", property);
    },
    content
  );
}
function setCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}
function setOrCreate(selector, init, content) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    init(el);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function injectLDJSON(id, obj) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}
function toAbs(url) {
  try {
    return new URL(url, window.location.origin).toString();
  } catch {
    return url;
  }
}

/* --- tiny inline icons --- */
function iconX() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M18.9 2H22l-7.7 8.8L22 22h-6.3l-4.9-6-5.6 6H2l8.5-9.4L2 2h6.4l4.4 5.4L18.9 2z"
      />
    </svg>
  );
}
function iconLinkedIn() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1 4.98 2.12 4.98 3.5zM0 8h5v16H0V8zm7.5 0h4.8v2.2h.1c.7-1.2 2.3-2.5 4.7-2.5 5 0 5.9 3.3 5.9 7.6V24h-5V16.4c0-1.8 0-4.1-2.5-4.1s-2.9 2-2.9 4v7.7h-5V8z"
      />
    </svg>
  );
}
function iconFacebook() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.7-3.9 1.1 0 2.2.2 2.2.2v2.4h-1.2c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0 0 22 12"
      />
    </svg>
  );
}
